/**
 * Gameplay surface materials.
 *
 * A material's gameplay behaviour is defined by `kind` and `behaviors`, never
 * by its visual colour. Visual data is carried alongside so the renderer can
 * draw it, but no gameplay code reads `visual`.
 */

export type SurfaceKind = 'safe' | 'lethal' | 'reactive' | 'trigger';

/**
 * Modular reactive behaviours. A reactive surface references zero or more of
 * these; each is interpreted by the movement rule that owns its `type`.
 */
export type ReactiveBehavior =
  | { type: 'traction'; accelScale: number; momentumDecayScale: number; turnScale: number }
  | { type: 'bend'; strength: number }
  | { type: 'compression'; strength: number }
  | { type: 'coupling'; strength: number };

export type ReactiveBehaviorType = ReactiveBehavior['type'];

export interface SurfaceVisual {
  /** sRGB hex colour, e.g. 0x5b3b8c. */
  color: number;
  emissive: number;
  emissiveIntensity: number;
  roughness: number;
  /** Procedural pattern hint for the renderer. */
  pattern: 'marble' | 'stone' | 'brick' | 'rune' | 'sparkle' | 'hazard' | 'glyph' | 'none';
  opacity: number;
}

export interface SurfaceMaterialDef {
  /** Stable identifier referenced by level data, e.g. "safe.stone". */
  id: string;
  kind: SurfaceKind;
  behaviors: readonly ReactiveBehavior[];
  visual: SurfaceVisual;
}

const v = (
  color: number,
  pattern: SurfaceVisual['pattern'],
  emissive = 0x000000,
  emissiveIntensity = 0,
  roughness = 0.85,
  opacity = 1,
): SurfaceVisual => ({ color, emissive, emissiveIntensity, roughness, pattern, opacity });

/** Built-in materials. Levels may add more through their own `materials` list. */
export const BUILTIN_MATERIALS: readonly SurfaceMaterialDef[] = [
  { id: 'safe.stone', kind: 'safe', behaviors: [], visual: v(0x2b2733, 'stone') },
  { id: 'safe.marble', kind: 'safe', behaviors: [], visual: v(0x4a2f6b, 'marble', 0x000000, 0, 0.55) },
  { id: 'safe.greenstone', kind: 'safe', behaviors: [], visual: v(0x2c3a33, 'stone') },
  { id: 'lethal.basic', kind: 'lethal', behaviors: [], visual: v(0xb3121b, 'hazard', 0xff1a1a, 0.9, 0.6) },
  {
    id: 'reactive.glide',
    kind: 'reactive',
    behaviors: [{ type: 'traction', accelScale: 0.25, momentumDecayScale: 0.1, turnScale: 0.4 }],
    visual: v(0x1b6f7a, 'glyph', 0x29e3ff, 0.35, 0.3),
  },
  {
    id: 'reactive.grip',
    kind: 'reactive',
    behaviors: [{ type: 'traction', accelScale: 1.6, momentumDecayScale: 4, turnScale: 1.8 }],
    visual: v(0x1f5560, 'glyph', 0x29e3ff, 0.2, 0.9),
  },
  { id: 'reactive.bend', kind: 'reactive', behaviors: [{ type: 'bend', strength: 1 }], visual: v(0x167f8f, 'glyph', 0x33f0ff, 0.5, 0.4) },
  {
    id: 'reactive.compression',
    kind: 'reactive',
    behaviors: [{ type: 'compression', strength: 1 }],
    visual: v(0x177a86, 'glyph', 0x7ff6ff, 0.6, 0.4),
  },
  { id: 'reactive.coupling', kind: 'reactive', behaviors: [{ type: 'coupling', strength: 1 }], visual: v(0x14707e, 'glyph', 0x40d8ff, 0.45, 0.4) },
  { id: 'trigger.start', kind: 'trigger', behaviors: [], visual: v(0x3cff8a, 'none', 0x3cff8a, 0.6, 1, 0.12) },
  { id: 'trigger.finish', kind: 'trigger', behaviors: [], visual: v(0xffd166, 'none', 0xffd166, 0.6, 1, 0.16) },
  { id: 'trigger.checkpoint', kind: 'trigger', behaviors: [], visual: v(0xa77bff, 'none', 0xa77bff, 0.6, 1, 0.12) },
];

export class MaterialRegistry {
  private readonly byId = new Map<string, SurfaceMaterialDef>();

  constructor(defs: readonly SurfaceMaterialDef[] = BUILTIN_MATERIALS) {
    for (const d of defs) this.register(d);
  }

  register(def: SurfaceMaterialDef): void {
    const errors = validateMaterial(def);
    if (errors.length) throw new Error(`Invalid material "${def.id}": ${errors.join('; ')}`);
    if (this.byId.has(def.id)) throw new Error(`Duplicate material id "${def.id}"`);
    this.byId.set(def.id, def);
  }

  get(id: string): SurfaceMaterialDef {
    const m = this.byId.get(id);
    if (!m) throw new Error(`Unknown material id "${id}"`);
    return m;
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  all(): IterableIterator<SurfaceMaterialDef> {
    return this.byId.values();
  }
}

export function validateMaterial(def: SurfaceMaterialDef): string[] {
  const errors: string[] = [];
  if (!/^[a-z0-9]+(\.[a-z0-9-]+)+$/.test(def.id)) errors.push('id must look like "kind.name"');
  if (!['safe', 'lethal', 'reactive', 'trigger'].includes(def.kind)) errors.push(`unknown kind ${String(def.kind)}`);
  if (def.kind !== 'reactive' && def.behaviors.length > 0) errors.push('only reactive materials may declare behaviors');
  const seen = new Set<string>();
  for (const b of def.behaviors) {
    if (seen.has(b.type)) errors.push(`behavior ${b.type} declared twice`);
    seen.add(b.type);
    for (const [k, val] of Object.entries(b)) {
      if (k !== 'type' && !(typeof val === 'number' && Number.isFinite(val) && val >= 0)) {
        errors.push(`behavior ${b.type}.${k} must be a non-negative number`);
      }
    }
  }
  return errors;
}

/** Looks up a behaviour of a given type on a material, or null. */
export function findBehavior<T extends ReactiveBehaviorType>(
  mat: SurfaceMaterialDef,
  type: T,
): Extract<ReactiveBehavior, { type: T }> | null {
  for (const b of mat.behaviors) if (b.type === type) return b as Extract<ReactiveBehavior, { type: T }>;
  return null;
}
